import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ClosableModalWithErrorResponseModel } from '../../base/closable-modal-with-error-response-model';
import { ModalService, ConfirmationModalComponent } from '../../../modules/modal';
import {
  CopyBackgroundInfoOption,
  CopyBackgroundInfoModalPayload,
  CopyBackgroundInfoPayload,
  CopyBackgroundInfoErrorResponse,
  BackgroundInfoType,
  Message
} from '../../../models';

import { BackgroundInfoService, NotificationService } from '../../../services';
import { TranslationConstants } from '../../../constants/translation';

@Component({
  selector: 'copy-background-info-modal',
  templateUrl: './copy-background-info-modal.component.html',
})
export class CopyBackgroundInfoModalComponent
  extends ClosableModalWithErrorResponseModel<CopyBackgroundInfoErrorResponse> implements OnInit, OnDestroy {
  data: CopyBackgroundInfoModalPayload;
  potentialSources: CopyBackgroundInfoOption[] = [];
  selectedSourceObjectId: number;

  useCaseTranslationKey: string;
  identifierTranslationKey: string;

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _backgroundInfoService: BackgroundInfoService,
    private _translate: TranslateService
  ) {
    super(notificationService, modalService);
  }

  ngOnInit(): void {
    this.useCaseTranslationKey = this._backgroundInfoService.getTranslationKeyByBackInfoType(this.data.useCase);
    this.identifierTranslationKey = this.getIdentifierTranslationKey();
    this.getPotentialSources();
    this.listenForErrorMessages();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  save(): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    this.modalService.openModal({
      component: ConfirmationModalComponent,
      data: {
        title: TranslationConstants.GLOBAL_CONFIRM,
        content: TranslationConstants.BACKGROUND_INFO_COPY_WARNING,
      } as Message,
      success: () => {
        this.copyBackgroundInfo();
      },
      cancel: () => {
        this.isSaveDisabled = false;
      },
    });
  }

  getUncontrolledLabel(uncontrolled: boolean): string {
    return uncontrolled ? `(${this._translate.instant(TranslationConstants.GENERAL_UNCONTROLLED_ABBREV)})` : '';
  }

  private copyBackgroundInfo(): void {
    const payload = this.getPayloadForSave();
    this._backgroundInfoService.copyBackgroundInfo(this.data.destinationObjectId, this.data.useCase, payload)
      .subscribe(() => {
        this.success();
        this.closeAllModals();
      });
  }

  private getPotentialSources(): void {
    let jurisdictionId;
    if (this.data.jurisdiction) {
      jurisdictionId = this.data.jurisdiction.countryId;
    }
    this._backgroundInfoService
      .getSourcesList(this.data.destinationObjectId, this.data.useCase, jurisdictionId)
      .subscribe(({ data }) => {
        this.potentialSources = data;
      });
  }

  private getPayloadForSave(): CopyBackgroundInfoPayload {
    const payload = new CopyBackgroundInfoPayload();
    payload.sourceId = this.selectedSourceObjectId;
    const selectedSource = this.potentialSources.find(potentialSource => potentialSource.id === this.selectedSourceObjectId);
    payload.uncontrolled = selectedSource && selectedSource.uncontrolled ? selectedSource.uncontrolled : false;
    if (this.data.jurisdiction) {
      payload.jurisdictionId = this.data.jurisdiction.countryId;
    }

    return payload;
  }

  private getIdentifierTranslationKey(): string {
    return this.data.useCase === BackgroundInfoType.Entity ?
      TranslationConstants.GLOBAL_CODE :
      TranslationConstants.GLOBAL_ID;
  }
}
